'use client';

import { changePassword } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useDialogStore } from '../ui/dialog';
import { ProfileSuccessPasswordChangeDialog } from '../profile/profile-success-password-change-dialog';
import { Field } from '@eliadmin/core/src/components/form/field';
import { MdLockOutline } from 'react-icons/md';

export const changePasswordSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      password_old: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type ChangePasswordFormProps = {
  onSubmitSuccess?: () => void;
};

export function ChangePasswordForm(props: ChangePasswordFormProps) {
  const { t } = useTranslations();

  const openDialog = useDialogStore(s => s.open);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof changePasswordSchema>>>({
    resolver: zodResolver(changePasswordSchema(formValidationMessages)),
    defaultValues: {
      password_old: '',
      password: '',
      password_confirmation: '',
    },
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const { toast } = useToast();
  const authToken = useCustomerStore(s => s.me!.authToken);

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      /** TODO: preklad */
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    if (data.password_old === data.password) {
      /** TODO: preklad */
      form.setError('password', { type: 'manual', message: t('text1103') });
      return;
    }

    try {
      await changePassword({ ...data, authToken, locale });
      openDialog({
        id: ProfileSuccessPasswordChangeDialog.id,
        content: <ProfileSuccessPasswordChangeDialog />,
      });
      props.onSubmitSuccess?.();
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text650'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        className={cn(
          'mx-auto flex w-full flex-col items-center justify-center gap-8 sm:max-w-[688px] sm:gap-10 lg:gap-12',
          'ea-change-password-form',
        )}
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            'grid w-full grid-cols-2 gap-4 sm:gap-5 lg:gap-6',
            'ea-change-password-form__fields',
          )}
        >
          <Field
            required
            control={form.control}
            name="password_old"
            placeholder={t('text3415')}
            type="password"
            className={{ wrapper: 'col-span-2 w-full' }}
            before={<MdLockOutline size={20} className="text-[#9CA3AF]" />}
          />
          <Field
            required
            control={form.control}
            name="password"
            placeholder={t('text203')}
            type="password"
            className={{ wrapper: 'col-span-2 w-full sm:col-span-1' }}
            before={<MdLockOutline size={20} className="text-[#9CA3AF]" />}
          />
          <Field
            required
            control={form.control}
            name="password_confirmation"
            placeholder={t('text3183')}
            type="password"
            className={{ wrapper: 'col-span-2 w-full sm:col-span-1' }}
            before={<MdLockOutline size={20} className="text-[#9CA3AF]" />}
          />
        </div>
        <Button
          type="submit"
          className={cn(
            'mx-auto w-full sm:w-fit',
            'ea-change-password-form__submit-button',
          )}
          isLoading={form.formState.isSubmitting}
        >
          {t('text3715')}
        </Button>
      </form>
    </Form>
  );
}
